import type {
  RequiredKeysOf,
  OperationRequestBodyContent,
  IsOperationRequestBodyOptional,
} from 'openapi-typescript-helpers';

import type { paths } from './generated/schema.gen';

import { buildApiUrl } from './url';
import { toFormData } from './form-data';

// ----------------------------------------------------------------------

export type ApiMethod = 'get' | 'post' | 'put' | 'delete';

/** One entry of the generated `endpoints` table. */
export type ApiEndpoint = {
  method: ApiMethod;
  path: keyof paths;
  body?: 'multipart';
  response?: 'file' | 'stream';
};

type Operation<E extends ApiEndpoint> =
  paths[E['path']] extends Record<E['method'], infer Op> ? Op : never;

type ParamsOption<Op> = Op extends { parameters: infer Params }
  ? RequiredKeysOf<Params> extends never
    ? { params?: Params }
    : { params: Params }
  : { params?: never };

type BodyOption<Op> =
  OperationRequestBodyContent<Op> extends never
    ? { body?: never }
    : IsOperationRequestBodyOptional<Op> extends true
      ? { body?: OperationRequestBodyContent<Op> }
      : { body: OperationRequestBodyContent<Op> };

/** Path, query and header values, the body, and the transport's own options, each typed by the endpoint's operation. */
export type ApiRequest<E extends ApiEndpoint, Options = never> = ParamsOption<Operation<E>> &
  BodyOption<Operation<E>> & { options?: Options };

type JsonResponse<Op> = Op extends {
  responses: { 200: { content: { 'application/json': infer Body } } };
}
  ? Body
  : Op extends { responses: { 200: { content?: never } } }
    ? void
    : unknown;

type NoContent<Op> = Op extends { responses: { 204: unknown } } ? null : never;

/**
 * The file of a file endpoint, otherwise the 200 JSON body: void for actions that answer with no content, and null
 * when an action that may return nothing answers 204.
 */
export type ApiResponse<E extends ApiEndpoint> = E extends { response: 'file' }
  ? Blob
  : JsonResponse<Operation<E>> | NoContent<Operation<E>>;

/**
 * What a transport sends: the URL already carries the path values and the query, and a multipart body is already
 * FormData. `headers` are the operation's own header parameters, which a transport sends on top of any headers in
 * its options. A `blob` response type asks for the raw file instead of parsed JSON. A 204 answer resolves null.
 */
export type ApiTransportRequest<Options> = {
  method: ApiMethod;
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
  responseType: 'json' | 'blob';
  options?: Options;
};

/** Sends one request and resolves with the response body. */
export type ApiTransport<Options> = (request: ApiTransportRequest<Options>) => Promise<unknown>;

type ResolvableEndpoint = ApiEndpoint & { response?: 'file' };

type RequestArgs<E extends ApiEndpoint, Options> =
  RequiredKeysOf<ApiRequest<E, Options>> extends never
    ? [request?: ApiRequest<E, Options>]
    : [request: ApiRequest<E, Options>];

type UrlArgs<E extends ApiEndpoint> =
  RequiredKeysOf<ParamsOption<Operation<E>>> extends never
    ? [request?: ParamsOption<Operation<E>>]
    : [request: ParamsOption<Operation<E>>];

type UntypedRequest<Options> = {
  params?: {
    path?: Record<string, unknown>;
    query?: Record<string, unknown>;
    header?: Record<string, unknown>;
  };
  body?: unknown;
  options?: Options;
};

// Header parameters the operation declares, as strings; unset ones are left off rather than sent empty.
function toHeaders(header?: Record<string, unknown>): Record<string, string> | undefined {
  if (!header) return undefined;

  const entries = Object.entries(header).filter(([, value]) => value !== undefined && value !== null);
  return entries.length ? Object.fromEntries(entries.map(([name, value]) => [name, String(value)])) : undefined;
}

// ----------------------------------------------------------------------

export async function sendApiRequest<Response, Options>(
  transport: ApiTransport<Options>,
  endpoint: ApiEndpoint,
  request?: object
): Promise<Response> {
  const { params, body, options }: UntypedRequest<Options> = request ?? {};

  return transport({
    method: endpoint.method,
    url: buildApiUrl(endpoint.path, params),
    body: endpoint.body === 'multipart' && body ? toFormData(body) : body,
    headers: toHeaders(params?.header),
    responseType: endpoint.response === 'file' ? 'blob' : 'json',
    options,
  }) as Promise<Response>;
}

/**
 * The URL of a request, for a link the browser or another tool follows itself (a download link, an event stream); it
 * takes only the path and query values.
 */
export function apiUrl<E extends ApiEndpoint>(endpoint: E, ...[request]: UrlArgs<E>): string {
  const untyped: object = request ?? {};
  const { params }: UntypedRequest<never> = untyped;

  return buildApiUrl(endpoint.path, params);
}

/**
 * The Web API, typed end to end from its OpenAPI document: the endpoint picks the operation, and the operation types
 * the params, the body, and the response. The transport decides how a request travels (an app's axios instance, fetch).
 * An event-stream endpoint is read as it arrives, with fetch, so the client takes only JSON and file endpoints.
 *
 * @example const isFollowing = await api(endpoints.jobPostingFollowers.isFollowing, { params: { path: { jobId } } });
 */
export function createApiClient<Options>(transport: ApiTransport<Options>) {
  return <E extends ResolvableEndpoint>(endpoint: E, ...[request]: RequestArgs<E, Options>) =>
    sendApiRequest<ApiResponse<E>, Options>(transport, endpoint, request);
}
